import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EmployeeSuperValidation, EmployeeSuperValidationStatus, ImportLine, QuickSuperMatch, QuickSuperMember, XeroEmployee, XeroPayrollCalendar } from '../types';
import { calendarTypeLabel, employeePayrollCalendarId, employeePayrollCalendarType } from '../types';
import { importApi } from '../api/client';

function normName(s: string) {
  return s.toLowerCase().replace(/[^a-z ]/g, '').trim().replace(/\s+/g, ' ');
}

function hasNameMatch(contractorName: string, employees: XeroEmployee[]): boolean {
  const n = normName(contractorName);
  return employees.some((e) => {
    const full = normName(`${e.FirstName} ${e.LastName}`);
    const rev  = normName(`${e.LastName} ${e.FirstName}`);
    return full === n || rev === n || n.includes(full) || full.includes(n);
  });
}

interface ResultsTableProps {
  importId: number;
  lines: ImportLine[];
  employees: XeroEmployee[];
  superValidations: Record<string, EmployeeSuperValidation>;
  inferredCalendarType: 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY' | null;
  resolvedCalendarType: string | null;
  calendars: XeroPayrollCalendar[];
  onLinesChange: (lines: ImportLine[]) => void;
  selectedLineIds: Set<number>;
  onSelectionChange: (lineId: number, selected: boolean) => void;
  quickSuperMembers: QuickSuperMember[];
  quickSuperMatches: Record<string, QuickSuperMatch>;
  onQuickSuperChange: (line: ImportLine, memberId: number | null) => void;
  onValidateEmployees: (employeeIds: string[], forceRefresh?: boolean) => Promise<void>;
  onRefreshEmployees?: () => void;
}

function fmt(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? '—' : n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtRate(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? '' : n.toFixed(1);
}

export default function ResultsTable({
  importId,
  lines,
  employees,
  superValidations,
  inferredCalendarType,
  resolvedCalendarType,
  calendars,
  onLinesChange,
  selectedLineIds,
  onSelectionChange,
  quickSuperMembers,
  quickSuperMatches,
  onQuickSuperChange,
  onValidateEmployees,
  onRefreshEmployees,
}: ResultsTableProps) {
  const [saving, setSaving] = useState<Set<number>>(new Set());
  const [refreshing, setRefreshing] = useState(false);

  const activeEmployees = employees.filter((e) => e.Status === 'ACTIVE' || e.Status === 'Active' || !e.Status);
  const mappedValidationStatuses = lines
    .map((line) => line.xeroEmployeeId ? superValidations[line.xeroEmployeeId]?.superStatus ?? 'UNKNOWN' : null)
    .filter((status): status is EmployeeSuperValidationStatus => !!status);
  const checkingSuperCount = mappedValidationStatuses.filter((status) => status === 'CHECKING' || status === 'UNKNOWN').length;
  const noSuperCount = mappedValidationStatuses.filter((status) => status === 'MISSING').length;
  const errorSuperCount = mappedValidationStatuses.filter((status) => status === 'ERROR').length;
  // Width accounts for the longest employee name PLUS the longest status
  // suffix shown in the dropdown (e.g. " (super not checked)", " (No super
  // fund)", " (Fortnightly)"). The button shows just the name, but the
  // dropdown shares this width — without the suffix budget, options get
  // visibly cut off.
  const longestNameCh = Math.max(
    0,
    ...activeEmployees.map((e) => `${e.FirstName} ${e.LastName}`.length)
  );
  const employeeSelectWidthCh = Math.max(20, longestNameCh + 22);

  async function handleRateChange(line: ImportLine, newRate: string) {
    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate < 0 || rate > 100) return;

    setSaving((s) => new Set(s).add(line.id));
    try {
      const { data } = await importApi.updateLine(importId, line.id, { superRate: rate });
      onLinesChange(lines.map((l) => (l.id === line.id ? data.line : l)));
    } finally {
      setSaving((s) => { const next = new Set(s); next.delete(line.id); return next; });
    }
  }

  async function handleEmployeeChange(line: ImportLine, employeeId: string) {
    const employee = employees.find((e) => e.EmployeeID === employeeId);
    setSaving((s) => new Set(s).add(line.id));
    try {
      const { data } = await importApi.updateLine(importId, line.id, {
        xeroEmployeeId: employeeId || undefined,
        xeroEmployeeName: employee ? `${employee.FirstName} ${employee.LastName}` : undefined,
      });
      onLinesChange(lines.map((l) => (l.id === line.id ? data.line : l)));
      if (employeeId) await onValidateEmployees([employeeId], false);
    } finally {
      setSaving((s) => { const next = new Set(s); next.delete(line.id); return next; });
    }
  }

  async function handleRefresh() {
    if (!onRefreshEmployees) return;
    setRefreshing(true);
    try { await onRefreshEmployees(); } finally { setRefreshing(false); }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      {(checkingSuperCount > 0 || noSuperCount > 0 || errorSuperCount > 0 || onRefreshEmployees) && (
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
          <p className="text-xs text-slate-600">
            {checkingSuperCount > 0
              ? `Checking super fund details for ${checkingSuperCount} employee${checkingSuperCount > 1 ? 's' : ''}...`
              : noSuperCount > 0
              ? `${noSuperCount} employee${noSuperCount > 1 ? 's' : ''} missing super fund in Xero after full detail fetch.`
              : errorSuperCount > 0
              ? `${errorSuperCount} employee super fund check${errorSuperCount > 1 ? 's' : ''} could not be verified.`
              : 'All employees have super funds configured.'}
          </p>
          {onRefreshEmployees && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="ml-4 flex-shrink-0 rounded border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              {refreshing ? 'Refreshing…' : 'Refresh employees'}
            </button>
          )}
        </div>
      )}
      <div className="overflow-x-auto overflow-y-visible">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-100/80 text-left">
              <Th>Contractor</Th>
              <Th right>Debit total</Th>
              <Th center>Super %</Th>
              <Th right>Super</Th>
              <Th>Xero employee / QuickSuper member</Th>
              <Th>Pay frequency</Th>
              <Th center sticky>Send to Xero</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {lines.map((line) => (
              <Row
                key={line.id}
                line={line}
                employees={activeEmployees}
                superValidations={superValidations}
                inferredCalendarType={inferredCalendarType}
                resolvedCalendarType={resolvedCalendarType}
                calendars={calendars}
                employeeSelectWidthCh={employeeSelectWidthCh}
                isSaving={saving.has(line.id)}
                onRateChange={handleRateChange}
                onEmployeeChange={handleEmployeeChange}
                onValidateEmployees={onValidateEmployees}
                isSelected={selectedLineIds.has(line.id)}
                onSelectionChange={onSelectionChange}
                quickSuperMembers={quickSuperMembers}
                quickSuperMatch={quickSuperMatches[line.id]}
                onQuickSuperChange={onQuickSuperChange}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({
  children,
  right,
  center,
  sticky,
}: {
  children: React.ReactNode;
  right?: boolean;
  center?: boolean;
  sticky?: boolean;
}) {
  return (
    <th
      className={[
        'whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-600',
        right ? 'text-right' : center ? 'text-center' : 'text-left',
        sticky ? 'sticky right-0 z-10 border-l border-slate-200 bg-slate-100' : '',
      ].join(' ')}
    >
      {children}
    </th>
  );
}

interface RowProps {
  line: ImportLine;
  employees: XeroEmployee[];
  superValidations: Record<string, EmployeeSuperValidation>;
  inferredCalendarType: 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY' | null;
  resolvedCalendarType: string | null;
  calendars: XeroPayrollCalendar[];
  employeeSelectWidthCh: number;
  isSaving: boolean;
  onRateChange: (line: ImportLine, rate: string) => void;
  onEmployeeChange: (line: ImportLine, employeeId: string) => void;
  onValidateEmployees: (employeeIds: string[], forceRefresh?: boolean) => Promise<void>;
  isSelected: boolean;
  onSelectionChange: (lineId: number, selected: boolean) => void;
  quickSuperMembers: QuickSuperMember[];
  quickSuperMatch: QuickSuperMatch | undefined;
  onQuickSuperChange: (line: ImportLine, memberId: number | null) => void;
}

function Row({
  line,
  employees,
  superValidations,
  inferredCalendarType,
  resolvedCalendarType,
  calendars,
  employeeSelectWidthCh,
  isSaving,
  onRateChange,
  onEmployeeChange,
  onValidateEmployees,
  isSelected,
  onSelectionChange,
  quickSuperMembers,
  quickSuperMatch,
  onQuickSuperChange,
}: RowProps) {
  const [localRate, setLocalRate] = useState(fmtRate(line.superRate));
  const [employeeMenuOpen, setEmployeeMenuOpen] = useState(false);
  const [employeeMenuRect, setEmployeeMenuRect] = useState<DOMRect | null>(null);
  const employeeMenuRef = useRef<HTMLDivElement | null>(null);
  const employeeButtonRef = useRef<HTMLButtonElement | null>(null);
  const isMapped = !!line.xeroEmployeeId;

  const mappedEmployee = employees.find((e) => e.EmployeeID === line.xeroEmployeeId);
  const validation = line.xeroEmployeeId ? superValidations[line.xeroEmployeeId] : undefined;
  const superStatus: EmployeeSuperValidationStatus = isMapped ? validation?.superStatus ?? 'UNKNOWN' : 'UNKNOWN';
  const hasValidSuper = !isMapped || superStatus === 'VALID';
  const mappedCalendar = calendars.find((calendar) => calendar.PayrollCalendarID === employeePayrollCalendarId(mappedEmployee));
  const payFrequency = employeePayrollCalendarType(mappedEmployee) ?? mappedCalendar?.CalendarType?.toUpperCase() ?? resolvedCalendarType ?? inferredCalendarType;
  const hasCalendarMismatch = !!resolvedCalendarType && !!payFrequency && payFrequency !== resolvedCalendarType;
  const noMatch = !isMapped && employees.length > 0 && !hasNameMatch(line.contractorName, employees);
  const employeeLabel = mappedEmployee
    ? `${mappedEmployee.FirstName} ${mappedEmployee.LastName}`
    : 'Select employee';

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      const target = event.target as Node;
      if (
        !employeeMenuRef.current?.contains(target) &&
        !employeeButtonRef.current?.contains(target)
      ) {
        setEmployeeMenuOpen(false);
      }
    }

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    if (!employeeMenuOpen) return;

    function syncPosition() {
      const rect = employeeButtonRef.current?.getBoundingClientRect();
      if (rect) setEmployeeMenuRect(rect);
    }

    syncPosition();
    window.addEventListener('resize', syncPosition);
    window.addEventListener('scroll', syncPosition, true);
    return () => {
      window.removeEventListener('resize', syncPosition);
      window.removeEventListener('scroll', syncPosition, true);
    };
  }, [employeeMenuOpen]);

  const previousSuperStatus = useRef<EmployeeSuperValidationStatus | null>(null);
  useEffect(() => {
    if (!isMapped) {
      previousSuperStatus.current = null;
      return;
    }

    const previousStatus = previousSuperStatus.current;
    if (previousStatus === superStatus) return;

    console.log('[UI SuperCheck]', {
      contractorName: line.contractorName,
      employeeId: mappedEmployee?.EmployeeID ?? line.xeroEmployeeId,
      employeeName: mappedEmployee ? `${mappedEmployee.FirstName} ${mappedEmployee.LastName}` : line.xeroEmployeeName,
      previousStatus,
      nextStatus: superStatus,
      SuperDataSource: mappedEmployee?.SuperDataSource,
      SuperDetailLoaded: mappedEmployee?.SuperDetailLoaded ?? mappedEmployee?.superDetailLoaded,
      payTemplateSuperLineCount: mappedEmployee?.PayTemplate?.SuperLines?.length ?? 0,
      superMembershipCount: validation?.superMemberships?.length ?? 0,
      warningBeingShown: superStatus === 'MISSING',
    });

    previousSuperStatus.current = superStatus;
  }, [
    isMapped,
    line.contractorName,
    line.xeroEmployeeId,
    line.xeroEmployeeName,
    mappedEmployee,
    validation?.superMemberships?.length,
    superStatus,
  ]);

  return (
    <tr className={['group transition-colors hover:bg-slate-50', isSaving ? 'bg-slate-50 opacity-80' : ''].join(' ')}>
      <td className="whitespace-nowrap px-4 py-2.5 font-medium text-slate-950">
        {line.contractorName}
      </td>

      <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-slate-900 tabular-nums">
        ${fmt(line.debitTotal)}
      </td>

      <td className="whitespace-nowrap px-4 py-2.5 text-center">
        <div className="inline-flex h-7 min-w-[5.25rem] items-center overflow-hidden rounded-md border border-slate-300 bg-white">
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={localRate}
            onChange={(e) => setLocalRate(e.target.value)}
            onBlur={(e) => {
              const formatted = fmtRate(e.target.value);
              setLocalRate(formatted);
              onRateChange(line, formatted);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const formatted = fmtRate(localRate);
                setLocalRate(formatted);
                onRateChange(line, formatted);
              }
            }}
            disabled={isSaving}
            className="h-full w-16 border-0 bg-transparent px-2 text-right text-sm tabular-nums text-slate-950 focus:outline-none focus:ring-0 disabled:bg-slate-100"
          />
          <span className="border-l border-slate-200 bg-slate-50 px-2 text-xs font-medium text-slate-500">%</span>
        </div>
      </td>

      <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono font-semibold text-slate-950 tabular-nums">
        {isSaving ? <span className="text-slate-400">...</span> : `$${fmt(line.superAmount)}`}
      </td>

      <td className="px-4 py-2.5" style={{ width: `${employeeSelectWidthCh}ch` }}>
        {!isSelected ? (
          <QuickSuperMemberCell
            line={line}
            members={quickSuperMembers}
            match={quickSuperMatch}
            width={employeeSelectWidthCh}
            disabled={isSaving}
            onChange={onQuickSuperChange}
          />
        ) : (
        <>
        <div className="relative">
          <button
            ref={employeeButtonRef}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              if (!isSaving && employees.length > 0) {
                const rect = employeeButtonRef.current?.getBoundingClientRect();
                if (rect) setEmployeeMenuRect(rect);
                setEmployeeMenuOpen((value) => !value);
              }
            }}
            disabled={isSaving || employees.length === 0}
            style={{ width: `${employeeSelectWidthCh}ch` }}
            className={[
              'flex h-8 items-center rounded-md border bg-white px-3 pr-8 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100',
              isMapped && !hasValidSuper
                ? 'border-slate-300 text-slate-900'
                : isMapped
                ? 'border-slate-300 text-slate-900'
                : 'border-slate-300 text-slate-700',
            ].join(' ')}
          >
            <span className="truncate">{employeeLabel}</span>
          </button>
          <svg className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
          {employeeMenuOpen && employeeMenuRect && createPortal(
            <div
              ref={employeeMenuRef}
              className="z-50 max-h-64 overflow-y-auto rounded-md border border-slate-300 bg-white shadow-lg"
              style={{
                position: 'fixed',
                top: employeeMenuRect.bottom + 4,
                left: employeeMenuRect.left,
                width: employeeMenuRect.width,
              }}
            >
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setEmployeeMenuOpen(false);
                  onEmployeeChange(line, '');
                }}
                className={[
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                  !line.xeroEmployeeId
                    ? 'bg-slate-100 font-semibold text-slate-950'
                    : 'text-slate-700 hover:bg-slate-50',
                ].join(' ')}
              >
                <SelectionCheck visible={!line.xeroEmployeeId} />
                <span className="truncate">Select employee</span>
              </button>
              {employees.map((employee) => {
                const status = superValidations[employee.EmployeeID]?.superStatus ?? 'UNKNOWN';
                const optionCalendar = calendars.find((calendar) => calendar.PayrollCalendarID === employeePayrollCalendarId(employee));
                const employeeFrequency = employeePayrollCalendarType(employee) ?? optionCalendar?.CalendarType?.toUpperCase();
                const disabledForFrequency =
                  !!resolvedCalendarType && !!employeeFrequency && employeeFrequency !== resolvedCalendarType;
                const selected = employee.EmployeeID === line.xeroEmployeeId;
                return (
                  <button
                    key={employee.EmployeeID}
                    type="button"
                    disabled={disabledForFrequency}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (disabledForFrequency) return;
                      setEmployeeMenuOpen(false);
                      onEmployeeChange(line, employee.EmployeeID);
                    }}
                    className={[
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                      disabledForFrequency ? 'cursor-not-allowed opacity-45' : '',
                      selected
                        ? 'bg-slate-100 font-semibold text-slate-950'
                        : 'text-slate-700 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <SelectionCheck visible={selected} />
                    <span className="truncate">
                      {employee.FirstName} {employee.LastName}
                      {status === 'UNKNOWN' ? <span className="ml-1 text-xs text-slate-500">(super not checked)</span> : ''}
                      {status === 'CHECKING' ? <span className="ml-1 text-xs text-slate-500">(checking...)</span> : ''}
                      {status === 'MISSING' ? <span className="ml-1 text-xs text-amber-600">(No super fund)</span> : ''}
                      {status === 'ERROR' ? <span className="ml-1 text-xs text-red-600">(Could not verify)</span> : ''}
                      {disabledForFrequency ? <span className="ml-1 text-xs text-slate-500">({calendarTypeLabel(employeeFrequency)})</span> : ''}
                    </span>
                  </button>
                );
              })}
            </div>,
            document.body
          )}
        </div>
        {isMapped && (superStatus === 'CHECKING' || superStatus === 'UNKNOWN') && (
          <p className="mt-1 text-xs text-slate-500">Checking super fund...</p>
        )}
        {isMapped && superStatus === 'MISSING' && (
          <p className="mt-1 text-xs text-amber-600">No super fund found after full detail fetch</p>
        )}
        {isMapped && superStatus === 'ERROR' && (
          <div className="mt-1 flex items-center gap-2 text-xs text-red-600">
            <span>{validation?.message ?? 'Could not verify super fund.'}</span>
            <button
              type="button"
              onClick={() => line.xeroEmployeeId && onValidateEmployees([line.xeroEmployeeId], true)}
              className="font-medium underline"
            >
              Retry
            </button>
          </div>
        )}
        {isMapped && hasCalendarMismatch && (
          <p className="mt-1 text-xs text-amber-700">
            {calendarTypeLabel(payFrequency)} employee does not match the {calendarTypeLabel(resolvedCalendarType)} pay run.
          </p>
        )}
        {noMatch && (
          <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
            <span className="font-bold">✕</span>
            No Xero employee found matching "{line.contractorName}" — select manually.
          </p>
        )}
        </>
        )}
      </td>

      <td className="whitespace-nowrap px-4 py-2.5 text-sm text-slate-700">
        <span className={hasCalendarMismatch ? 'font-medium text-amber-700' : ''}>
          {payFrequency ? calendarTypeLabel(payFrequency) : '—'}
        </span>
      </td>

      <td className="sticky right-0 z-10 whitespace-nowrap border-l border-slate-200 bg-white px-4 py-2.5 text-center group-hover:bg-slate-50">
        <input
          type="checkbox"
          checked={isSelected}
          disabled={isSaving}
          onChange={(event) => onSelectionChange(line.id, event.target.checked)}
          aria-label={`Send ${line.contractorName} to Xero`}
          className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-40"
        />
      </td>
    </tr>
  );
}

function QuickSuperMemberCell({
  line,
  members,
  match,
  width,
  disabled,
  onChange,
}: {
  line: ImportLine;
  members: QuickSuperMember[];
  match: QuickSuperMatch | undefined;
  width: number;
  disabled: boolean;
  onChange: (line: ImportLine, memberId: number | null) => void;
}) {
  if (members.length === 0) {
    return (
      <p className="text-xs text-slate-500" style={{ maxWidth: `${width}ch` }}>
        Not sent to Xero. Drop your QuickSuper Employees.csv into the import box to choose a QuickSuper member.
      </p>
    );
  }
  const memberId = match?.memberId ?? null;
  return (
    <div>
      <div className="relative">
        <select
          value={memberId ?? ''}
          disabled={disabled}
          onChange={(event) => onChange(line, event.target.value ? Number(event.target.value) : null)}
          style={{ width: `${width}ch` }}
          className="h-8 appearance-none rounded-md border border-slate-300 bg-white px-3 pr-8 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
        >
          <option value="">Select QuickSuper member</option>
          {members.map((member) => (
            <option key={member.id} value={member.id}>
              {member.name}{member.fundName ? ` - ${member.fundName}` : ''}
            </option>
          ))}
        </select>
        <svg className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </div>
      {memberId === null ? (
        <p className="mt-1 text-xs text-amber-700">QuickSuper member needed for the contribution file.</p>
      ) : (
        <p className="mt-1 text-xs text-slate-500">
          QuickSuper{match?.source === 'auto' ? ' (matched by name)' : ' (remembered)'}
        </p>
      )}
    </div>
  );
}

function SelectionCheck({ visible }: { visible: boolean }) {
  return (
    <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center text-slate-500">
      {visible && (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.25 7.25a1 1 0 01-1.42 0l-3.25-3.25a1 1 0 111.42-1.42l2.54 2.54 6.54-6.54a1 1 0 011.42 0z" clipRule="evenodd" />
        </svg>
      )}
    </span>
  );
}
